import { Component, EventEmitter, Input, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type TextFieldSurface = 'surface' | 'surface-container';

export type TextFieldSize = 'md' | 'dense';

const SIZE_CLASSES: Record<TextFieldSize, string> = {
  md: 'h-11',
  dense: 'h-[38px]',
};

let nextId = 0;

@Component({
  selector: 'aav-text-field',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TextFieldComponent), multi: true },
  ],
  host: { class: 'block' },
  template: `
    <div [class]="containerClasses">
      <aav-icon
        *ngIf="leadingIcon"
        [name]="leadingIcon"
        [size]="20"
        [class]="invalid ? 'text-aav-error' : 'text-[rgb(var(--aav-slate-400))]'"
      ></aav-icon>

      <input
        [id]="inputId"
        [type]="type"
        [value]="value"
        [placeholder]="placeholder"
        [disabled]="disabled"
        [attr.name]="name || null"
        [attr.autocomplete]="autocomplete || null"
        [attr.maxlength]="maxlength || null"
        [attr.required]="required || null"
        [attr.aria-invalid]="invalid"
        [attr.aria-describedby]="footerText ? footerId : null"
        [class]="inputClasses"
        (input)="onInput($event)"
        (focus)="onFocus()"
        (blur)="onBlur()"
      />

      <button
        *ngIf="trailingIcon"
        type="button"
        [class]="trailingIconClasses"
        [attr.aria-label]="trailingIconLabel || null"
        [disabled]="disabled"
        (click)="trailingIconClick.emit()"
      >
        <aav-icon [name]="trailingIcon" [size]="20"></aav-icon>
      </button>

      <label [for]="inputId" [class]="labelClasses">
        {{ label }}<ng-container *ngIf="required">&nbsp;*</ng-container>
      </label>
    </div>

    <p *ngIf="footerText" [id]="footerId" [class]="footerClasses">{{ footerText }}</p>
  `,
})
export class TextFieldComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() type: 'text' | 'email' | 'password' | 'tel' | 'number' | 'date' = 'text';
  @Input() placeholder = '';
  @Input() helperText = '';
  @Input() errorMessage = '';
  @Input() invalid = false;
  @Input() required = false;
  @Input() disabled = false;
  @Input() leadingIcon = '';
  @Input() trailingIcon = '';
  @Input() trailingIconLabel = '';
  @Input() autocomplete = '';
  @Input() name = '';
  @Input() maxlength: number | null = null;
  @Input() uppercase = false;

  @Input() labelBackground: TextFieldSurface = 'surface';

  @Input() size: TextFieldSize = 'md';

  @Output() trailingIconClick = new EventEmitter<void>();

  readonly inputId = `aav-text-field-${nextId++}`;
  readonly footerId = `${this.inputId}-footer`;

  value = '';
  focused = false;

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  get inputClasses(): string {
    const base =
      'w-full bg-transparent text-aav-body-medium text-aav-on-surface outline-none ' +
      'placeholder:text-aav-on-surface-variant disabled:cursor-not-allowed';

    return this.uppercase ? `${base} uppercase placeholder:normal-case` : base;
  }

  get containerClasses(): string {
    const base =
      `relative flex ${SIZE_CLASSES[this.size]} items-center gap-2 rounded-aav-sm ` +
      'bg-transparent px-[12px] ring-inset transition duration-150';

    if (this.disabled) {
      return `${base} cursor-not-allowed ring-1 ring-aav-outline-variant opacity-40`;
    }
    if (this.invalid) {
      return `${base} ring-2 ring-aav-error`;
    }
    if (this.focused) {
      return `${base} ring-2 ring-aav-primary shadow-aav-focus`;
    }
    return `${base} ring-1 ring-aav-outline-variant hover:ring-aav-on-surface-variant/40`;
  }

  get labelClasses(): string {
    const base =
      'pointer-events-none absolute -top-2 left-2.5 px-[5px] text-aav-label-small-strong leading-[13px]';
    const background =
      this.labelBackground === 'surface-container' ? 'bg-aav-surface-container' : 'bg-aav-surface';

    if (this.invalid && !this.disabled) {
      return `${base} ${background} text-aav-error`;
    }
    if (this.focused && !this.disabled) {
      return `${base} ${background} text-aav-primary`;
    }
    return `${base} ${background} text-aav-on-surface-variant`;
  }

  get trailingIconClasses(): string {
    const base = 'flex h-5 w-5 shrink-0 items-center justify-center transition-colors';
    return this.invalid
      ? `${base} text-aav-error`
      : `${base} text-aav-on-surface-variant hover:text-aav-on-surface`;
  }

  get footerText(): string {
    return this.invalid && this.errorMessage ? this.errorMessage : this.helperText;
  }

  get footerClasses(): string {
    const base = 'mt-1 px-1 text-aav-body-small';
    return this.invalid && this.errorMessage
      ? `${base} text-aav-error`
      : `${base} text-aav-on-surface-variant`;
  }

  onInput(event: Event): void {
    this.value = (event.target as HTMLInputElement).value;
    this.onChange(this.value);
  }

  onFocus(): void {
    this.focused = true;
  }

  onBlur(): void {
    this.focused = false;
    this.onTouched();
  }

  writeValue(value: string | null): void {
    this.value = value ?? '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
